import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  GraduationCap, ArrowRight, ShieldCheck, Award, Users, Sparkles, Phone, Mail,
} from "lucide-react";

/**
 * Courses — Education section landing page.
 * Renders the 4 official course banners AS-IS (no crop) inside elegant frames.
 * Content follows the original banner artwork; pricing + enroll CTAs are wired
 * to /contact for lead capture.
 */

const COURSES = [
  {
    key: "ai",
    img: "/course-banners/ai.png",
    tag: "Future Skills",
    title: "AI Course",
    tagline: "Learn. Apply. Lead the Future.",
    subtitle: "Master Artificial Intelligence from Basics to Real-World Applications.",
    price: "₹7,999",
    priceStrike: null,
    highlights: ["AI Fundamentals", "Python for AI", "Machine Learning", "Deep Learning", "Generative AI & LLMs", "AI Tools & Applications"],
    accent: "from-royal via-indigo-700 to-royal",
  },
  {
    key: "dca-dtp-tally",
    img: "/course-banners/dca-dtp-tally.png",
    tag: "3-in-1 Power Pack",
    title: "DCA + DTP + Tally Course",
    tagline: "Skills Today, Success Tomorrow.",
    subtitle: "Learn. Practice. Get Certified. Build Your Career.",
    price: "₹7,999",
    priceStrike: null,
    highlights: ["Diploma in Computer Apps", "Desktop Publishing", "TallyPrime with GST", "MS Office Suite", "CorelDRAW & Photoshop", "Career Guidance"],
    accent: "from-royal-deep via-royal to-royal-deep",
  },
  {
    key: "office",
    img: "/course-banners/office.png",
    tag: "Smart Skills · Better Jobs",
    title: "Office Package Course",
    tagline: "Master Essential Tools. Boost Productivity.",
    subtitle: "Learn MS Word, Excel, PowerPoint, Outlook & More — Become Office-Ready!",
    price: "₹7,999",
    priceStrike: null,
    highlights: ["MS Word", "MS Excel", "MS PowerPoint", "MS Outlook", "Google Docs & Sheets", "Bonus Modules"],
    accent: "from-emerald-800 via-royal to-emerald-900",
  },
  {
    key: "accounting",
    img: "/course-banners/accounting.png",
    tag: "Master Numbers · Build Success",
    title: "Accounting Course",
    tagline: "From Basics to Advanced — Become a Complete Accounting Professional.",
    subtitle: "12 modules covering everything from Journals to GST & TDS.",
    price: "₹11,999",
    priceStrike: null,
    highlights: ["Basics of Accounting", "Journal & Ledger", "GST & TDS Accounting", "Payroll", "Financial Analysis", "Tally · Excel · Busy · Marg"],
    accent: "from-royal via-royal-deep to-plum",
  },
];

function CourseCard({ course }) {
  return (
    <article
      data-testid={`course-${course.key}`}
      className="group relative bg-white border border-line rounded-sm overflow-hidden hover:border-gold/60 transition-all shadow-[0_1px_2px_rgba(11,61,145,0.04)] hover:shadow-[0_16px_40px_-10px_rgba(11,61,145,0.20)]"
    >
      {/* Header strip */}
      <div className={`relative px-6 py-4 bg-gradient-to-r ${course.accent} text-white`}>
        <div className="absolute inset-0 opacity-[0.10] mix-blend-overlay [background-image:repeating-linear-gradient(45deg,#fff_0_1px,transparent_1px_8px)]" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.3em] uppercase text-gold/90 mb-0.5">{course.tag}</div>
            <h3 className="font-serif text-xl sm:text-2xl text-white leading-tight truncate">{course.title}</h3>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[9px] tracking-[0.3em] uppercase text-gold/80">Fee</div>
            <div className="text-xl sm:text-2xl text-gold font-extrabold tabular-nums lining-nums">{course.price}</div>
          </div>
        </div>
      </div>

      {/* Banner — full, uncropped */}
      <div className="p-3 sm:p-4 bg-gradient-to-b from-ivory/40 to-white">
        <div className="relative bg-white border-2 border-gold/30 rounded-sm p-1.5 sm:p-2 shadow-[inset_0_0_0_1px_rgba(11,61,145,0.06)]">
          <div className="relative overflow-hidden rounded-sm border border-royal/20">
            <img
              src={course.img}
              alt={course.title}
              loading="lazy"
              className="block w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.01]"
            />
          </div>
        </div>

        {/* Highlights row */}
        <div className="mt-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">What you&rsquo;ll learn</div>
          <div className="flex flex-wrap gap-2">
            {course.highlights.map((h) => (
              <span
                key={h}
                className="text-[11px] px-2.5 py-1 border border-line rounded-sm text-royal/80 bg-ivory/60 hover:border-gold/40 hover:text-royal transition-colors"
              >
                {h}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-5 pt-4 border-t border-dashed border-line flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-navy/60 italic leading-relaxed max-w-md">
            {course.tagline}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              data-testid={`course-${course.key}-enroll`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-gold to-gold-deep text-royal hover:from-royal hover:to-royal-deep hover:text-gold transition-all rounded-sm font-medium text-sm shadow-sm"
            >
              Enroll Now <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Courses() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-royal-deep text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-royal-deep/95 via-royal/70 to-navy/95" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/15 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-gold mb-4">
            <GraduationCap size={16} /> Education · Digital Learning
          </div>
          <h1
            data-testid="courses-hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl"
          >
            Practical, career-ready courses
            <br />
            <span className="text-gold">built for the modern learner.</span>
          </h1>
          <p className="mt-6 text-white/85 max-w-3xl leading-relaxed">
            Choose from four job-oriented programmes across AI, Office Productivity,
            Computer &amp; Design and Accounting — all delivered with practical, hands-on
            training, expert support and a certificate of completion.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            {[
              { icon: Award,       l: "Certificate" },
              { icon: Users,       l: "Expert Trainers" },
              { icon: ShieldCheck, l: "Lifetime Access" },
              { icon: Sparkles,    l: "Job Assistance" },
            ].map((b) => (
              <div key={b.l} className="flex items-center gap-2 px-3 py-2 bg-white/[0.06] border border-white/15 rounded-sm">
                <b.icon size={14} className="text-gold shrink-0" />
                <span className="text-xs text-white/85">{b.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Our Courses</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-royal leading-tight">
              4 programmes. Real skills. Ready careers.
            </h2>
          </div>
          <div className="text-sm text-navy/60 max-w-sm">
            Every course includes practical training, downloadable resources and a
            recognised certificate — enrol once, learn for life.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {COURSES.map((c) => (
            <CourseCard course={c} key={c.key} />
          ))}
        </div>
      </section>

      {/* Contact strip */}
      <section className="bg-ivory border-y border-line py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Not sure which course fits?</div>
            <h3 className="font-serif text-2xl sm:text-3xl text-royal leading-tight">
              Talk to a Samara Wiz counsellor — free.
            </h3>
            <p className="text-navy/60 mt-2 text-sm max-w-lg">
              Share your goal, and we&rsquo;ll help you pick the right course and timeline.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="tel:+919860128999"
              className="inline-flex items-center gap-2 px-5 py-3 border border-royal text-royal hover:bg-royal hover:text-white transition-colors rounded-sm text-sm font-medium"
            >
              <Phone size={14} /> +91 98601 28999
            </a>
            <a
              href="mailto:support@samarawiz.com"
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-royal to-royal-deep text-white hover:from-royal-deep hover:to-royal transition-all rounded-sm text-sm font-medium shadow-sm"
            >
              <Mail size={14} /> support@samarawiz.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
